import { useTranslation } from "react-i18next";
import { Button } from "@mantine/core";
import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { IconFilterOff } from "@tabler/icons-react";

import { ScrollArea } from "@components/ScrollArea";

import { truncateWithEllipsis } from "@utils/truncateText";

import { getDays, getMonths } from "@data/timelineFilters";

import { StyledDialogOverlay } from "@styles/components/Dialog.styled";

import { StyledDocumentationFilterDialogContent } from "./DocumentationFilterDialog.styled";


interface FilterDialogProps {
  filters: {
    year: string;
    month: string;
    day: string;
    physician: string;
    // fileType: string;
  };
  setFilters: (arg: FilterDialogProps["filters"]) => void;
  physicians: Set<string>;
}

const years = ["2025", "2024", "2023", "2022"]; // todo: Get this from backend or add logic to always factor in current year

export const DocumentationFilterDialog = ({
  filters,
  setFilters,
  physicians,
}: FilterDialogProps) => {
  const { t } = useTranslation();

  const newPhysicians = [...physicians];

  return (
    <Dialog.Portal>
      <StyledDialogOverlay />

      <StyledDocumentationFilterDialogContent>
        <div className="dialog-header">
          <Dialog.Title className="DialogTitle">
            {t("table-filters")}
          </Dialog.Title>

          <Dialog.Close asChild>
            <button className="IconButton" aria-label="Close">
              <Cross2Icon />
            </button>
          </Dialog.Close>
        </div>

        <div className="filters-wrapper">
          <form className="filter-column">
            <p>{t("year")}</p>
            <RadioGroup.Root
              className="RadioGroupRoot"
              // defaultValue={years[0]}
              onValueChange={value => setFilters({ ...filters, year: value })}
              value={filters.year}
              aria-label="Years">
              <button
                className="clear-filter-btn"
                type="button"
                onClick={() => setFilters({ ...filters, year: "" })}>
                {t("clear")}
              </button>
              {/* <div className="clear-filter-btn">&nbsp;</div> */}
              {years.map((year, index) => (
                <div key={index}>
                  <RadioGroup.Item
                    className="RadioGroupItem"
                    value={year}
                    id={year}>
                    <RadioGroup.Indicator className="RadioGroupIndicator" />
                  </RadioGroup.Item>
                  <label htmlFor={year}>{year}</label>
                </div>
              ))}
            </RadioGroup.Root>
          </form>

          <form className="filter-column">
            <p>{t("month")}</p>
            <RadioGroup.Root
              className="RadioGroupRoot"
              // defaultValue={months[0]}
              onValueChange={value => setFilters({ ...filters, month: value })}
              value={filters.month}
              aria-label="Months">
              <button
                className="clear-filter-btn"
                type="button"
                onClick={() => setFilters({ ...filters, month: "" })}>
                {t("clear")}
              </button>
              {getMonths().map((month, index) => (
                <div key={index}>
                  <RadioGroup.Item
                    className="RadioGroupItem"
                    value={month.value}
                    id={month.value}>
                    <RadioGroup.Indicator className="RadioGroupIndicator" />
                  </RadioGroup.Item>
                  <label htmlFor={month.value}>{month.label}</label>
                </div>
              ))}
            </RadioGroup.Root>
          </form>

          <form className="filter-column">
            <p>{t("day")}</p>
            <RadioGroup.Root
              className="RadioGroupRoot"
              // defaultValue={days[0]}
              onValueChange={value => setFilters({ ...filters, day: value })}
              value={filters.day}
              aria-label="Day of the week">
              <button
                className="clear-filter-btn"
                type="button"
                onClick={() => setFilters({ ...filters, day: "" })}>
                {t("clear")}
              </button>
              {getDays().map((day, index) => (
                <div key={index}>
                  <RadioGroup.Item
                    className="RadioGroupItem"
                    value={day.value}
                    id={day.value}>
                    <RadioGroup.Indicator className="RadioGroupIndicator" />
                  </RadioGroup.Item>
                  <label htmlFor={day.value}>{day.label}</label>
                </div>
              ))}
            </RadioGroup.Root>
          </form>

          <form className="filter-column">
            <p>{t("user")}</p>
            <RadioGroup.Root
              className="RadioGroupRoot"
              // defaultValue={categories[0]}
              onValueChange={value =>
                setFilters({ ...filters, physician: value })
              }
              value={filters.physician}
              aria-label="User">
              <button
                className="clear-filter-btn"
                type="button"
                onClick={() => setFilters({ ...filters, physician: "" })}
                disabled={newPhysicians.length === 0}>
                {t("clear")}
              </button>
              {newPhysicians.length ? (
                <ScrollArea
                  scrollbarOrientation="vertical"
                  className="scrollbar">
                  <div className="physicians-wrapper">
                    {newPhysicians.map((physician, index) => (
                      <div key={index}>
                        <RadioGroup.Item
                          className="RadioGroupItem"
                          value={physician}
                          id={physician}>
                          <RadioGroup.Indicator className="RadioGroupIndicator" />
                        </RadioGroup.Item>
                        <label htmlFor={physician}>
                          {truncateWithEllipsis(physician, 15)}
                        </label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <p className="empty-label">N/A</p>
              )}
            </RadioGroup.Root>
          </form>

          {/* <form className="filter-column">
            <p>{t("file-type")}</p>
            <RadioGroup.Root
              className="RadioGroupRoot"
              // defaultValue={categories[0]}
              onValueChange={value =>
                setFilters({ ...filters, fileType: value })
              }
              value={filters.fileType}
              aria-label="file type">
              <button
                className="clear-filter-btn"
                type="button"
                onClick={() => setFilters({ ...filters, fileType: "" })}>
                {t("clear")}
              </button>
              {fileTypes.map((fileType, index) => (
                <div key={index}>
                  <RadioGroup.Item
                    className="RadioGroupItem"
                    value={fileType}
                    id={fileType}>
                    <RadioGroup.Indicator className="RadioGroupIndicator" />
                  </RadioGroup.Item>
                  <label htmlFor={fileType}>{fileType}</label>
                </div>
              ))}
            </RadioGroup.Root>
          </form> */}
        </div>

        <div className="dialog-footer">
          <Button
            variant="transparent"
            size="compact-sm"
            onClick={() =>
              setFilters({
                year: "",
                month: "",
                day: "",
                physician: "",
                // fileType: "",
              })
            }
            leftSection={<IconFilterOff size={16} />}>
            {t("clear-filters")}
          </Button>
          <Dialog.Close asChild>
            <Button>{t("done")}</Button>
          </Dialog.Close>
        </div>
      </StyledDocumentationFilterDialogContent>
    </Dialog.Portal>
  );
};
